import { Box, IconButton } from '@mui/material';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';
import DatePickerField from '../../../common/DatePickerField';
import EditIcon from '../../../common/icons/EditIcon';
import TextField from '../../../common/TextField';
import { formatDate } from '../../../../utils/date';
import { ApplicantProfileData } from '../../../../pages/rec/applicant/ApplicantProfile';
import { usePermissions } from '../../../../context/PermissionContext';
import { PERMISSION_IDS } from '../../../../constants/permissions';
import { isValidEmail, toText } from '../../../../utils/common';

interface ApplicantProfileInfoCardProps {
  values: ApplicantProfileData;
  setValues: (values: ApplicantProfileData) => void;
  isEditMode: boolean;
  setIsEditMode?: (isEditMode: boolean) => void;
  emailError?: string;
  setEmailError?: (error: string) => void;
}

const ApplicantProfileInfoCard: FC<ApplicantProfileInfoCardProps> = ({
  setIsEditMode,
  setValues,
  values,
  isEditMode,
  emailError = '',
  setEmailError,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const [localEmailError, setLocalEmailError] = useState('');
  const displayEmailError = emailError || localEmailError;
  const updateEmailError = setEmailError ?? setLocalEmailError;
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (isEditMode === false) {
      setIsEdit(false);
    }
  }, [isEditMode]);

  return (
    <Box className="view-section">
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: '-12px', right: '-12px' }}>
          {!isEdit && hasPermission(PERMISSION_IDS.APPLICANT_PROFILE_EDIT) && (
            <IconButton
              className="view-edit-icon"
              onClick={() => {
                setIsEdit(true);
                setIsEditMode?.(true);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
        <Box className="view-row-box">
          <Box className="view-column-box">
            <div className="view-label-title">Date of Birth</div>
            {isEdit ? <DatePickerField
              className="view-column-edit-box"
              value={values.basicInfo.dob ? dayjs(values.basicInfo.dob) : null}
              onChange={(date: string | null) => setValues({ ...values, basicInfo: { ...values.basicInfo, dob: date ? date : '' } })}
              maxDate={dayjs().subtract(1, 'day')}
            /> : <div className="view-value-title">{values.basicInfo.dob ? formatDate(values.basicInfo.dob) : 'N/A'}</div>}
          </Box>
          <Box className="view-column-box">
            <div className="view-label-title">NIC</div>
            {isEdit ? <TextField
              className="view-column-edit-box"
              value={toText(values.basicInfo.nic)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValues({ ...values, basicInfo: { ...values.basicInfo, nic: e.target.value } })}
            /> : <div className="view-value-title">{toText(values.basicInfo.nic) || 'N/A'}</div>}
          </Box>
          <Box className="view-column-box">
            <div className="view-label-title">Email</div>
            {isEdit ? <TextField
              className="view-column-edit-box"
              value={toText(values.basicInfo.email)}
              error={!!displayEmailError}
              helperText={displayEmailError}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                updateEmailError('');
                setValues({ ...values, basicInfo: { ...values.basicInfo, email: e.target.value } });
              }}
              onBlur={() => {
                const email = (values.basicInfo.email ?? '').trim();
                if (email && !isValidEmail(email)) {
                  updateEmailError('Please enter a valid email address');
                }
              }}
            /> : <div className="view-value-title">{toText(values.basicInfo.email) || 'N/A'}</div>}
          </Box>
        </Box>
        <Box className="view-row-box" sx={{ mt: 2 }}>
          <Box className="view-column-box">
            <div className="view-label-title">Contact Number (Mobile)</div>
            {isEdit ? <TextField
              inputMode="tel"
              className="view-column-edit-box"
              value={toText(values.basicInfo.contactNoMobile)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const sanitized = e.target.value.replace(/[^+\-\d]/g, '');
                setValues({ ...values, basicInfo: { ...values.basicInfo, contactNoMobile: sanitized } });
              }}
            /> : <div className="view-value-title">{toText(values.basicInfo.contactNoMobile) || 'N/A'}</div>}
          </Box>
          <Box className="view-column-box">
            <div className="view-label-title">Contact Number (Residence)</div>
            {isEdit ? <TextField
              inputMode="tel"
              className="view-column-edit-box"
              value={toText(values.basicInfo.contactNoResident)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const sanitized = e.target.value.replace(/[^+\-\d]/g, '');
                setValues({ ...values, basicInfo: { ...values.basicInfo, contactNoResident: sanitized } });
              }}
            /> : <div className="view-value-title">{toText(values.basicInfo.contactNoResident) || 'N/A'}</div>}
          </Box>
          <Box className="view-column-box">
            <div className="view-label-title">Address</div>
            {isEdit ? <TextField
              className="view-column-edit-box"
              value={toText(values.basicInfo.address)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValues({ ...values, basicInfo: { ...values.basicInfo, address: e.target.value } })}
            /> : <div className="view-value-title">{toText(values.basicInfo.address) || 'N/A'}</div>}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ApplicantProfileInfoCard;